import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
    Typography,
    TablePagination,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    MoreVert as MoreVertIcon,
    Info as InfoIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import OrderHistoryModal from './OrderHistoryModal';
import { getAllExecutions, getFilledExecutions, getCancelledExecutions, getRejectedExecutions } from '../../api/executionApi';

const ExecutionHistory = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [directionFilter, setDirectionFilter] = useState('all');
    const [periodFilter, setPeriodFilter] = useState('current-month');
    const [startDate, setStartDate] = useState('2025-09-01');
    const [endDate, setEndDate] = useState('2025-09-30');
    const [anchorEl, setAnchorEl] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderHistoryModalOpen, setOrderHistoryModalOpen] = useState(false);
    
    // API data states
    const [executionData, setExecutionData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [executionType, setExecutionType] = useState('all'); // all, filled, cancelled, rejected
    
    // Column visibility settings
    const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);
    const [columnVisibility, setColumnVisibility] = useState({
        id: true,
        symbol: true,
        quantity: true,
        volume: true,
        direction: true,
        entryPrice: true,
        tp: false,
        sl: false,
        commission: false,
        closingCommission: false,
        conversionFee: false,
        created: true,
        lastModified: false,
        margin: false,
        swap: false,
        channel: false,
        label: false,
        comment: false,
        pips: false,
        gross: false,
        net: true
    });

    // Fetch executions from API
    const fetchExecutions = async (type = 'all') => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found');
                return;
            }

            let data = [];
            switch (type) {
                case 'filled':
                    data = await getFilledExecutions(token);
                    break;
                case 'cancelled':
                    data = await getCancelledExecutions(token);
                    break;
                case 'rejected':
                    data = await getRejectedExecutions(token);
                    break;
                default:
                    data = await getAllExecutions(token);
                    break;
            }
            
            console.log('Execution data fetched:', data);
            setExecutionData(data);
        } catch (error) {
            console.error('Error fetching executions:', error);
            setError('Failed to fetch execution data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on component mount and when execution type changes
    useEffect(() => {
        fetchExecutions(executionType);
    }, [executionType]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Column settings handlers
    const handleColumnSettingsClick = (event) => {
        setColumnSettingsAnchor(event.currentTarget);
    };

    const handleColumnSettingsClose = () => {
        setColumnSettingsAnchor(null);
    };

    const handleColumnToggle = (column) => {
        setColumnVisibility(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const handleResetToDefault = () => {
        setColumnVisibility({
            id: true,
            symbol: true,
            quantity: true,
            volume: true,
            direction: true,
            entryPrice: true,
            tp: false,
            sl: false,
            commission: false,
            closingCommission: false,
            conversionFee: false,
            created: true,
            lastModified: false,
            margin: false,
            swap: false,
            channel: false,
            label: false,
            comment: false,
            pips: false,
            gross: false,
            net: true
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatBalance = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    return (
        <Box sx={{ 
            backgroundColor: '#1a1a1a', 
            color: 'white', 
            minHeight: '100%',
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
                {/* Header Controls */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    marginBottom: 0,
                    flexWrap: 'nowrap',
                    padding: '6px 10px',
                    backgroundColor: '#0a0e1a',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    minHeight: '36px',
                    justifyContent: 'space-between',
                    overflow: 'hidden'
                }}>
                {/* Search Input */}
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: '#999', mr: 0.3, fontSize: 12 }} />,
                        }}
                        sx={{
                            minWidth: 100,
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'transparent',
                                color: 'white',
                                height: 26,
                                fontSize: '0.8rem',
                                fontWeight: 550,
                                letterSpacing: '0.05em',
                                borderRadius: '4px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                '& fieldset': {
                                    border: 'none',
                                },
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.25)',
                                },
                                '&.Mui-focused': {
                                    borderColor: '#06b6d4',
                                },
                                '& input::placeholder': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 550,
                                    letterSpacing: '0.05em',
                                    opacity: 1,
                                },
                            },
                        }}
                    />

                    {/* Direction Filter */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'nowrap', fontWeight: 550, letterSpacing: '0.05em' }}>Direction:</Typography>
                        <Select
                            value={directionFilter}
                            onChange={(e) => setDirectionFilter(e.target.value)}
                            size="small"
                            MenuProps={{
                                container: () => document.body,
                                disablePortal: false,
                                sx: {
                                    zIndex: 999999
                                },
                                slotProps: {
                                    root: {
                                        style: {
                                            zIndex: 999999,
                                            position: 'fixed'
                                        }
                                    }
                                },
                                PaperProps: {
                                    sx: {
                                        backgroundColor: '#2a2a2a',
                                        color: 'white',
                                        zIndex: 999999,
                                        '& .MuiMenuItem-root': {
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#06b6d4',
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: 'transparent',
                                color: 'white',
                                height: 26,
                                fontSize: '0.8rem',
                                fontWeight: 550,
                                letterSpacing: '0.05em',
                                borderRadius: '4px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.25)',
                                },
                                '&.Mui-focused': {
                                    borderColor: '#06b6d4',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#999',
                                    fontSize: '14px',
                                },
                                minWidth: '110px',
                            }}
                        >
                            <MenuItem value="all" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>All directions</MenuItem>
                            <MenuItem value="buy" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Buy</MenuItem>
                            <MenuItem value="sell" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Sell</MenuItem>
                        </Select>
                    </Box>

                    {/* Period Filter */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'nowrap', fontWeight: 550, letterSpacing: '0.05em' }}>Period:</Typography>
                        <Select
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value)}
                            size="small"
                            MenuProps={{
                                container: () => document.body,
                                disablePortal: false,
                                sx: {
                                    zIndex: 999999
                                },
                                slotProps: {
                                    root: {
                                        style: {
                                            zIndex: 999999,
                                            position: 'fixed'
                                        }
                                    }
                                },
                                PaperProps: {
                                    sx: {
                                        backgroundColor: '#2a2a2a',
                                        color: 'white',
                                        zIndex: 999999,
                                        '& .MuiMenuItem-root': {
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#06b6d4',
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: 'transparent',
                                color: 'white',
                                height: 26,
                                fontSize: '0.8rem',
                                fontWeight: 550,
                                letterSpacing: '0.05em',
                                borderRadius: '4px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.25)',
                                },
                                '&.Mui-focused': {
                                    borderColor: '#06b6d4',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#999',
                                    fontSize: '14px',
                                },
                                minWidth: '115px',
                            }}
                        >
                            <MenuItem value="current-month" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Current month</MenuItem>
                            <MenuItem value="last-month" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Last month</MenuItem>
                            <MenuItem value="last-3-months" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Last 3 months</MenuItem>
                            <MenuItem value="custom" sx={{ fontWeight: 550, letterSpacing: '0.05em' }}>Custom range</MenuItem>
                        </Select>
                    </Box>

                    {/* Start Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'nowrap', fontWeight: 550, letterSpacing: '0.05em' }}>Start Date:</Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            sx={{
                                minWidth: 110,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    height: 26,
                                    fontSize: '0.8rem',
                                    fontWeight: 550,
                                    letterSpacing: '0.05em',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover': {
                                        borderColor: 'rgba(255, 255, 255, 0.25)',
                                    },
                                    '&.Mui-focused': {
                                        borderColor: '#06b6d4',
                                    },
                                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                        filter: 'invert(1)',
                                        cursor: 'pointer',
                                        opacity: 0.7,
                                        '&:hover': {
                                            opacity: 1,
                                        }
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* End Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'nowrap', fontWeight: 550, letterSpacing: '0.05em' }}>End Date:</Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            sx={{
                                minWidth: 110,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    height: 26,
                                    fontSize: '0.8rem',
                                    fontWeight: 550,
                                    letterSpacing: '0.05em',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover': {
                                        borderColor: 'rgba(255, 255, 255, 0.25)',
                                    },
                                    '&.Mui-focused': {
                                        borderColor: '#06b6d4',
                                    },
                                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                        filter: 'invert(1)',
                                        cursor: 'pointer',
                                        opacity: 0.7,
                                        '&:hover': {
                                            opacity: 1,
                                        }
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 0.5, 
                        marginLeft: 'auto',
                        flexWrap: 'nowrap'
                    }}>
                        {/* Column Settings Button */}
                        <IconButton
                            size="small"
                            onClick={handleColumnSettingsClick}
                            sx={{
                                color: '#06b6d4',
                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                borderRadius: '4px',
                                padding: '5px',
                                width: '26px',
                                height: '26px',
                                zIndex: 1,
                                position: 'relative',
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.2)',
                                    border: '1px solid rgba(6, 182, 212, 0.5)',
                                },
                                '&:active': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.3)',
                                }
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: 15 }} />
                        </IconButton>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setOrderHistoryModalOpen(true)}
                            sx={{
                                backgroundColor: '#06b6d4',
                                color: 'white',
                                height: 26,
                                fontSize: '0.7rem',
                                minWidth: 75,
                                borderRadius: '4px',
                                textTransform: 'none',
                                fontWeight: 550,
                                px: 1,
                                '&:hover': {
                                    backgroundColor: '#0891b2',
                                },
                            }}
                        >
                            Order history
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            endIcon={<ArrowDropDownIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                backgroundColor: '#06b6d4',
                                color: 'white',
                                height: 26,
                                fontSize: '0.7rem',
                                minWidth: 70,
                                borderRadius: '4px',
                                textTransform: 'none',
                                fontWeight: 550,
                                px: 1,
                                '&:hover': {
                                    backgroundColor: '#0891b2',
                                },
                            }}
                        >
                            Statement
                        </Button>
                    </Box>
            </Box>

            {/* Loading and Error States */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <CircularProgress sx={{ color: '#06b6d4' }} />
                </Box>
            )}

            {error && (
                <Box sx={{ padding: 2 }}>
                    <Alert severity="error" sx={{ backgroundColor: 'rgba(211, 47, 47, 0.1)', color: 'white' }}>
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Table */}
            <Paper sx={{
                boxShadow: "none",
                flex: 1,
                margin: '0 !important',
                padding: '0 !important',
                backgroundColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 350px)',
                overflow: 'hidden'
            }}>
                <TableContainer sx={{
                    maxHeight: '100%',
                    overflow: 'auto',
                    scrollbarWidth: 'none', /* Firefox */
                    msOverflowStyle: 'none', /* IE and Edge */
                    '&::-webkit-scrollbar': {
                        display: 'none' /* Chrome, Safari, Opera */
                    }
                }}>
                    <Table
                        sx={{
                            marginTop: '0 !important',
                            borderSpacing: "0px 0px",
                            borderCollapse: "separate",
                            '& .MuiTableCell-root': {
                                padding: '4px 8px',
                                fontSize: '11px',
                                height: 24,
                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                backgroundColor: 'rgba(6, 182, 212, 0.12)',
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 26,
                                color: '#06b6d4',
                                borderBottom: '1px solid rgba(6, 182, 212, 0.25)',
                                padding: '4px 8px',
                            },
                            '& .MuiTableRow-root': {
                                height: 24,
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.08)',
                                }
                            },
                        }}>
                        <TableHead>
                            <TableRow>
                                {columnVisibility.id && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
ID
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.symbol && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Symbol
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.quantity && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Quantity
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.volume && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Volume
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.direction && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Direction
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.entryPrice && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Entry price
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.tp && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
TP
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.sl && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
SL
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.commission && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Commission
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.closingCommission && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Closing commission
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.conversionFee && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Unr. conversion fee
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.created && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Created (UTC+0)
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.lastModified && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Last modified (UTC+0)
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.margin && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Margin
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.swap && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Swap
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.channel && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Channel
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.label && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Label
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.comment && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Comment
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.pips && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Pips
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.gross && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Gross $
                                        </Typography>
                                    </TableCell>
                                )}
                                {columnVisibility.net && (
                                    <TableCell>
                                        <Typography color="text.tableheader" sx={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.2 }}>
Net $
                                        </Typography>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {executionData.map((row, rowIdx) => {
                                // Alternating row background color (same as Positions)
                                const bgColor = rowIdx % 2 === 0 ? 'rgba(6, 182, 212, 0.10)' : '#181d23';
                                
                                // White text style for all data - compact and slim
                                const textStyle = {
                                    color: 'white',
                                    fontSize: '11px',
                                    lineHeight: 1.2
                                };

                                return (
                                    <TableRow key={row.id} sx={{ backgroundColor: bgColor }}>
                                        {columnVisibility.id && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.execId}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.symbol && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.symbolName}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.quantity && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.orderQty}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.volume && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.cumQty}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.direction && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.side}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.entryPrice && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{formatCurrency(row.avgPx)}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.tp && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.sl && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.commission && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.closingCommission && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.conversionFee && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.created && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{new Date(row.transactTime).toLocaleString()}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.lastModified && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{new Date(row.transactTime).toLocaleString()}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.margin && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.swap && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.channel && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.source}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.label && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.clOrdId}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.comment && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>{row.text || '-'}</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.pips && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.gross && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                        {columnVisibility.net && (
                                            <TableCell align="left">
                                                <Typography sx={textStyle}>-</Typography>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={executionData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color: 'white',
                        backgroundColor: '#2a2a2a',
                        borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                        padding: '8px 16px',
                        '& .MuiTablePagination-toolbar': {
                            color: 'white',
                            minHeight: '40px',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            color: 'white',
                            fontSize: '12px',
                        },
                        '& .MuiIconButton-root': {
                            color: '#06b6d4',
                            '&:hover': {
                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            }
                        },
                    }}
                />
            </Paper>

            {/* Column Settings Popover */}
            <Popover
                open={Boolean(columnSettingsAnchor)}
                anchorEl={columnSettingsAnchor}
                onClose={handleColumnSettingsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                container={() => document.body}
                disablePortal={false}
                sx={{
                    zIndex: 999999
                }}
                slotProps={{
                    root: {
                        style: {
                            zIndex: 999999,
                            position: 'fixed'
                        }
                    },
                    paper: {
                        style: {
                            zIndex: 999999
                        }
                    }
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: '#2a2a2a',
                        color: 'white',
                        borderRadius: '8px',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                        minWidth: 200,
                        maxHeight: 400,
                        overflow: 'auto'
                    }
                }}
            >
                <List sx={{ padding: 0 }}>
                    {/* Column Options */}
                    {[
                        { key: 'id', label: 'ID' },
                        { key: 'symbol', label: 'Symbol' },
                        { key: 'quantity', label: 'Quantity' },
                        { key: 'volume', label: 'Volume' },
                        { key: 'direction', label: 'Direction' },
                        { key: 'entryPrice', label: 'Entry price' },
                        { key: 'tp', label: 'TP' },
                        { key: 'sl', label: 'SL' },
                        { key: 'commission', label: 'Commission' },
                        { key: 'closingCommission', label: 'Closing commission' },
                        { key: 'conversionFee', label: 'Unr. conversion fee' },
                        { key: 'created', label: 'Created (UTC+0)' },
                        { key: 'lastModified', label: 'Last modified (UTC+0)' },
                        { key: 'margin', label: 'Margin' },
                        { key: 'swap', label: 'Swap' },
                        { key: 'channel', label: 'Channel' },
                        { key: 'label', label: 'Label' },
                        { key: 'comment', label: 'Comment' },
                        { key: 'pips', label: 'Pips' },
                        { key: 'gross', label: 'Gross $' },
                        { key: 'net', label: 'Net $' }
                    ].map((column) => (
                        <ListItem
                            key={column.key}
                            button
                            onClick={() => handleColumnToggle(column.key)}
                            sx={{
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {columnVisibility[column.key] ? (
                                    <Typography sx={{ color: 'white', fontSize: '14px' }}>✓</Typography>
                                ) : (
                                    <Typography sx={{ color: 'transparent', fontSize: '14px' }}>✓</Typography>
                                )}
                            </ListItemIcon>
                            <ListItemText 
                                primary={column.label}
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        color: 'white',
                                        fontSize: '13px',
                                        fontWeight: columnVisibility[column.key] ? 500 : 400
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                    
                    {/* Reset Button */}
                    <ListItem
                        button
                        onClick={handleResetToDefault}
                        sx={{
                            padding: '12px 16px',
                            borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                            backgroundColor: 'rgba(6, 182, 212, 0.05)',
                            '&:hover': {
                                backgroundColor: 'rgba(6, 182, 212, 0.15)',
                            }
                        }}
                    >
                        <ListItemText 
                            primary="Reset to default"
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: '#06b6d4',
                                    fontSize: '13px',
                                    fontWeight: 550,
                                    textAlign: 'center'
                                }
                            }}
                        />
                    </ListItem>
                </List>
            </Popover>

            {/* Order History Modal */}
            <OrderHistoryModal 
                open={orderHistoryModalOpen}
                onClose={() => setOrderHistoryModalOpen(false)}
            />
        </Box>
    );
};

export default ExecutionHistory;